import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';

interface SimpleConfirmationProps {
  isVisible: boolean;
}

/**
 * Simple confirmation view shown when company has disabled result visibility
 */
const SimpleConfirmation: React.FC<SimpleConfirmationProps> = ({ isVisible }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-3xl mx-auto">
          {/* Header with Logo */}
          <div className={`text-center mb-8 md:mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-10 h-10 bg-foreground rounded flex items-center justify-center">
                <span className="text-background font-bold text-lg">Z</span>
              </div>
              <span className="text-xl font-medium text-foreground">Zentrix Insights</span>
            </div>
            
            <div className="flex justify-center mb-6">
              <div className="bg-primary/10 p-4 rounded-full">
                <CheckCircle2 className="h-12 w-12 text-primary" />
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
              {t('results.companyResults.submittedTitle')}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t('results.companyResults.resultsSentSimple')}
            </p>
          </div>

          {/* What's Next Card */}
          <Card className={`border border-border bg-gradient-to-br from-primary/5 to-primary/10 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <CardContent className="py-6">
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 p-2 rounded-lg shrink-0">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{t('results.companyResults.whatsNext')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('results.companyResults.whatsNextDesc')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className={`mt-8 text-center transition-all duration-700 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <button 
              onClick={() => navigate('/')} 
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('results.companyResults.returnToHome')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleConfirmation;
